import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyTableState,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, MoreHorizontal, Trash2, User, Users } from 'lucide-react';
import { useState } from 'react';

import { formatShortDate } from '@/lib/datetime';
import { useTeamDetailContext } from './teamDetailContext';

interface TeamMember {
  id: string;
  displayName: string;
  isLeader: boolean;
  joinedAt: string;
  isOnline?: boolean;
  lastSeenAt?: string | null;
}

// Match the bordered-header card chrome used on the event Teams tab
// (TeamsList) so the team Members tab reads as the sibling listing.
const CARD_CLASSES = 'rounded-xl border bg-card shadow-sm py-0 gap-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b flex flex-row items-center justify-between';

/**
 * Lists every team member with leader badge + last-seen timestamp,
 * with a per-row dropdown for management actions (toggle captain,
 * remove from team). Visually mirrors `TeamsList` on the event Teams
 * tab so a facilitator moving between event ↔ team pages sees the
 * same grid + the same row-action affordance.
 */
export function EventTeamMembersTab() {
  const { eventId, teamId } = useTeamDetailContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const { data, isLoading } = useQuery<{ success: boolean; data: TeamMember[] }>({
    queryKey: ['EventTeamMembers', eventId, teamId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams/${teamId}/members`),
    enabled: !!eventId && !!teamId,
  });
  const members = data?.data ?? [];

  // Both mutations invalidate the same queries — the member list
  // itself plus the team summary (memberCount, captain) so the hero
  // metadata stays in sync after a change.
  const invalidateMemberQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['EventTeamMembers', eventId, teamId] });
    queryClient.invalidateQueries({ queryKey: ['EventTeam', eventId, teamId] });
    queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
  };

  const toggleCaptainMutation = useMutation({
    mutationFn: async (member: TeamMember) =>
      apiClient.patch(
        `/events/${eventId}/teams/${teamId}/members/${member.id}`,
        { isLeader: !member.isLeader },
      ),
    onSuccess: (_res, member) => {
      toast.success(member.isLeader ? 'Removed captain status' : 'Promoted to captain');
      invalidateMemberQueries();
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to update member'),
  });

  const removeMutation = useMutation({
    mutationFn: async (memberId: string) =>
      apiClient.delete(`/events/${eventId}/teams/${teamId}/members/${memberId}`),
    onSuccess: () => {
      toast.success('Member removed');
      invalidateMemberQueries();
      setRemoveTarget(null);
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to remove member'),
  });

  if (isLoading) {
    return (
      <Card className={CARD_CLASSES}>
        <CardHeader className={CARD_HEADER_CLASSES}>
          <div>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="mt-1 h-4 w-48" />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={CARD_CLASSES}>
        <CardHeader className={CARD_HEADER_CLASSES}>
          <div>
            <CardTitle>Members</CardTitle>
            <CardDescription>
              {members.length} member{members.length === 1 ? '' : 's'} on this team
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {members.length === 0 ? (
            <EmptyTableState
              icon={<Users className="h-8 w-8" />}
              title="No members yet"
              description="Players will appear here once they join with the team code."
            />
          ) : (
            <ul className="divide-y">
              {members.map((m) => (
                <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {m.displayName}
                      </span>
                      {m.isLeader && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-amber-900">
                          <Crown className="h-3 w-3" /> Captain
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Joined {formatShortDate(m.joinedAt)}</span>
                      {m.lastSeenAt && <span>Last seen {formatShortDate(m.lastSeenAt)}</span>}
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          aria-label={`Actions for ${m.displayName}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => toggleCaptainMutation.mutate(m)}
                          disabled={toggleCaptainMutation.isPending}>
                          <Crown className="mr-2 h-4 w-4" />
                          {m.isLeader ? 'Remove captain status' : 'Make captain'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setRemoveTarget(m)}
                          className="text-destructive focus:text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove from team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove member"
        description={`Are you sure you want to remove "${removeTarget?.displayName}" from this team? They can rejoin with the team code.`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (removeTarget) removeMutation.mutate(removeTarget.id);
        }}
      />
    </>
  );
}
